import "./TeamPage.scss";

import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ReactComponent as Add } from "../../assets/icons/add.svg";
import icons from "../../assets/sport-icons/_sportIcons";
import FollowButton from "../../components/essentials/btns/follow-btn/FollowButton";
import Button from "../../components/essentials/button/Button";
import ClickIcon from "../../components/essentials/icon-buttons/click-icon/ClickIcon";
import AcedSelect, {
	SelectOption,
} from "../../components/essentials/input/select/AcedSelect";
import AcedTextArea from "../../components/essentials/input/text-area/AcedTextArea";
import AcedInput from "../../components/essentials/input/text/AcedInput";
import AcedModal from "../../components/essentials/modals/aced-modal/AcedModal";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import { selectUser, setProfile, setUser } from "../../core/auth/authSlice";
import { FollowApiRoutes } from "../../shared/api-routes/member/follow-api.routes";
import { MembersApiRoutes } from "../../shared/api-routes/member/members-api.routes";
import { ProfilePictureApiRoutes } from "../../shared/api-routes/member/profile-picture.api-routes";
import { SeasonInfo, TeamData, User } from "../../shared/models";
import http from "../../shared/utils/http";
import SelectProfilePicture from "../complete-profile/profile-picture/SelectProfilePicture";
import FollowersModal from "../profile-page/components/followers-modal/FollowersModal";
import SeasonCard from "./components/season-card/SeasonCard";

import teamPlaceholder from "../../assets/images/userPlaceholder.png";
import { MsgButton } from "../../components/essentials/btns/msg-btn/MsgButton";
import { useIsTouchDevice } from "../../shared/customHooks";
import { getProfilePictureUrl } from "../../shared/utils/profilePictureUrl";
import { capitalizeName } from "../../shared/utils/util";

const TeamPage: React.FC = () => {
	const params = useParams();

	const [isToggleEnabled, setToggle] = useState(false);
	const [editBio, setEditBio] = useState(false);
	const [editStats, setEditStats] = useState(false);

	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { actorId, avatarVersion } = useSelector(selectUser) as User;

	const [teamData, setTeamData] = useState<TeamData>();
	const [seasonsArray, setSeasonsArray] = useState<SeasonInfo[]>([]);
	const [categories, setCategories] = useState<SelectOption[]>([]);
	const [leagues, setLeagues] = useState<SelectOption[]>([]);
	const [sportOptions, setSportOptions] = useState<SelectOption[]>([]);

	const {
		control,
		handleSubmit,
		setValue,
		getValues,
		reset,
		watch,
		formState: { errors, isDirty },
		register,
	} = useForm({
		mode: "onChange",
		defaultValues: {
			bio: teamData?.bio ?? "",
			website: teamData?.website ?? "",
			slogan: teamData?.slogan ?? "",
			category: teamData?.category ?? "",
			league: teamData?.league ?? "",
			city: teamData?.city ?? "",
			country: teamData?.country ?? "",
			sport: teamData?.sport ?? "",
		},
	});

	const watchCategory = watch("category");
	const watchSport = watch("sport");

	useEffect(() => {
		http
			.get(MembersApiRoutes.GET_USER(params.teamId))
			.then((res) => {
				setTeamData(res);
			})
			.catch((error) => {
				console.error("Error fetching team:", error);
			});

		// Fetch team seasons separately
		http
			.get(`/teams/${params.teamId}/seasons`)
			.then((res) => {
				setSeasonsArray(res || []);
			})
			.catch((error) => {
				console.error("Error fetching seasons:", error);
			});

		http.get(FollowApiRoutes.GET_USER_FOLLOWERS(params.teamId)).then((res) => {
			setFollowers(res.length);
		});
	}, [params.teamId]);

	// Set up categories when team data or watched sport changes
	useEffect(() => {
		const currentSport = watchSport || teamData?.sport;
		if (!currentSport) {
			setCategories([]);
			return;
		}

		const sportLabel = t("sportData.sportsLabels." + currentSport);
		setCategories([
			{ value: "M", label: `Male ${sportLabel}` },
			{ value: "F", label: `Female ${sportLabel}` },
		]);
	}, [teamData?.sport, watchSport, t]);

	// Set up leagues when category changes
	useEffect(() => {
		if (!teamData?.sport || !watchCategory) {
			setLeagues([]);
			return;
		}

		http.get(`/utils/leagues?category=${watchCategory}`).then((response) => {
			const leagueOptions: SelectOption[] = response.map(
				(value: { name: string; category: string; id: string }) =>
					({
						value: value.id,
						label: value.name,
					}) as SelectOption,
			);
			setLeagues(leagueOptions);
		});
	}, [teamData?.sport, watchCategory]);

	useEffect(() => {
		if (teamData) {
			reset({
				bio: teamData.bio ?? "",
				website: teamData.website ?? "",
				slogan: teamData.slogan ?? "",
				category: teamData.category ?? "",
				league: teamData.league?.id ?? "",
				city: teamData.city ?? "",
				country: teamData.country ?? "",
				sport: teamData.sport ?? "",
			});
		}
	}, [teamData, reset]);

	// Load sport options on mount
	useEffect(() => {
		http.get("/utils/sports").then((response) => {
			setSportOptions(
				response.map((sport: { name: string; value: string }) => ({
					value: sport.value,
					label: t("sportData.sportsLabels." + sport.value, sport.name),
				})),
			);
		});
	}, [t]);

	const onBioSubmit = (data: any) => {
		http
			.patch(`/members/${params.teamId}`, { bio: data.bio })
			.then((response) => {
				dispatch(setProfile({ bio: response.bio }));
				setTeamData((prevData) => ({
					...prevData!,
					bio: response.bio,
				}));

				setEditBio(false);
			})
			.catch((error) => console.log(error));
	};

	const [newSeason, setNewSeason] = useState(false);

	const removeSeasonAction = (index: number, id?: any) => {
		if (id) http.delete(`/teams/season/${id}`).then((response) => {});
		else setNewSeason(false);
		const _seasonsArray = seasonsArray?.filter((_, i) => i !== index);
		setSeasonsArray(_seasonsArray);
	};

	const onBasicInfoSubmit = (data: any) => {
		// Find the selected league object to get the name
		const selectedLeague = leagues.find(
			(league) => league.value === data.league,
		);

		http
			.patch(`/members/${params.teamId}`, {
				city: data.city,
				country: data.country,
				sport: data.sport,
				league: data.league
					? {
							name: selectedLeague?.label || data.league,
							category: data.category,
						}
					: null,
			})
			.then((response) => {
				dispatch(setProfile(response));
				setTeamData((prevData) => ({
					...prevData!,
					city: response.city,
					country: response.country,
					sport: response.sport,
					league: response.league,
					category: response.category,
				}));
				setBasicInfoModalOpen(false);
			})
			.catch((error) => console.log(error));
	};

	const onStatsSubmit = (data: any) => {
		// Find the selected league object to get the name
		const selectedLeague = leagues.find(
			(league) => league.value === data.league,
		);

		http
			.patch(`/members/${params.teamId}`, {
				website: data.website || null,
				slogan: data.slogan || null,
				league: data.league
					? {
							name: selectedLeague?.label || data.league,
							category: data.category,
						}
					: null,
			})
			.then((response) => {
				dispatch(setProfile(response));
				setTeamData(response);
				setEditStats(false);
			})
			.catch((error) => console.log(error));
	};

	const manageBioField = () => {
		if (!teamData?.bio && !editBio) {
			if (actorId === params.teamId)
				return (
					<>
						<div className="profile__bio">
							<Button
								className="btn btn-link profile__bio-add-btn"
								action={() => setEditBio(true)}
							>
								{t("profile.profilePage.addBioBtnLabel")}
								<Add
									style={{
										width: `20px`,
										height: `20px`,
										stroke: "white",
										marginRight: "8px",
									}}
								/>
							</Button>
						</div>
					</>
				);
		} else if (editBio) {
			return (
				<>
					<form
						className="profile__bio d-flex center column"
						onSubmit={handleSubmit(onBioSubmit)}
					>
						<AcedTextArea
							placeholder={t("profile.profilePage.addBioBtnLabel")}
							control={control}
							name="bio"
						/>

						<Button
							className="btn btn-primary profile__bio-submit"
							type="submit"
						>
							{t("profile.profilePage.addBioBtnLabel")}
						</Button>
					</form>
				</>
			);
		} else {
			return (
				<>
					<div className="profile__bio">
						<div className="d-flex justify-space-between">
							<p className=" profile__bio-title">
								{" "}
								{t("profile.sections.about")}
							</p>

							{actorId === params.teamId && (
								<ClickIcon
									icon="edit"
									onClick={() => setEditBio(true)}
									size={26}
								/>
							)}
						</div>
						<p className="profile__bio-box">{teamData?.bio}</p>
					</div>
				</>
			);
		}
	};

	const [followers, setFollowers] = useState<number>(0);

	useEffect(() => {
		window.scrollTo(0, 0);
		http.get(FollowApiRoutes.GET_USER_FOLLOWERS(params.teamId)).then((res) => {
			setFollowers(res.length);
		});
	}, [params.teamId]);

	const onUnfollow = () => {
		setFollowers((prevValue) => {
			return prevValue - 1;
		});
	};

	const onFollow = () => {
		setFollowers((prevValue) => {
			return prevValue + 1;
		});
	};

	const addSeason = () => {
		if (seasonsArray)
			setSeasonsArray([
				...seasonsArray,
				{
					year: "",
					description: "",
					isNew: true,
				},
			]);
		setNewSeason(true);
	};

	const [isFollowersModalOpen, setFollowersModalOpen] =
		useState<boolean>(false);

	const [isPictureModalOpen, setPictureModalOpen] = useState<boolean>(false);
	const [isBasicInfoModalOpen, setBasicInfoModalOpen] =
		useState<boolean>(false);

	const handlePictureSave = async (croppedBlob: Blob | null) => {
		let res;
		if (croppedBlob) {
			const formData = new FormData();
			formData.append("profilePicture", croppedBlob, "profile.png");

			res = await http.post(
				ProfilePictureApiRoutes.ADD_PROFILE_PIC(actorId),
				formData,
				{
					headers: { "Content-Type": "multipart/form-data" },
				},
			);
		} else {
			res = await http.delete(
				ProfilePictureApiRoutes.RESET_PROFILE_PIC(actorId),
			);
		}

		dispatch(setUser({ avatarVersion: res.avatarVersion }));
		setPictureModalOpen(false);
	};

	const [isProfilePictureHovered, setProfilePictureHovered] =
		useState<boolean>(false);

	const SelectSportIcon = () => {
		type SportIconName = keyof typeof icons;
		const sport = teamData?.sport as SportIconName | undefined;
		const Icon = sport && icons[sport];
		return Icon ? (
			<div className="profile__heading-sport-guy">
				<Icon />
			</div>
		) : null;
	};

	const isTouch = useIsTouchDevice();
	return (
		<>
			<Header />
			<div className="profile container">
				<div className="profile__heading">
					<div className="profile__heading-profile-picture-container">
						<div
							className="profile__heading-profile-picture"
							{...(actorId === params.teamId &&
								!isTouch && {
									onMouseEnter: () => setProfilePictureHovered(true),
									onMouseLeave: () => setProfilePictureHovered(false),
								})}
							{...(actorId === params.teamId &&
								isTouch && {
									onTouchStart: () => setProfilePictureHovered(true),
								})}
						>
							<img
								className="profile__heading-img"
								src={
									getProfilePictureUrl(
										params.teamId!,
										actorId === params.teamId
											? avatarVersion
											: teamData?.avatarVersion,
										teamData?.profilePictureUrl,
									) || teamPlaceholder
								}
								alt=""
								onError={(e) => (e.currentTarget.src = teamPlaceholder)}
							/>

							{isProfilePictureHovered && (
								<div className="img-actions">
									<ClickIcon
										size="m"
										icon={avatarVersion ? "edit" : "camera"}
										onClick={() => {
											setPictureModalOpen(true);
											setProfilePictureHovered(false);
										}}
									/>
								</div>
							)}
							{SelectSportIcon()}
						</div>

						<AcedModal
							state={{
								isModalOpen: isPictureModalOpen,
								setModalOpen: setPictureModalOpen,
							}}
						>
							<SelectProfilePicture
								onCancel={() => {
									setPictureModalOpen(false);
								}}
								onSave={handlePictureSave}
								initialImageUrl={getProfilePictureUrl(actorId, avatarVersion)}
								isTouch={isTouch}
							/>
						</AcedModal>
					</div>

					<div className="profile__heading-edit-section">
						{actorId === params.teamId && (
							<ClickIcon
								icon="edit"
								onClick={() => {
									setBasicInfoModalOpen(true);
								}}
								size={26}
							/>
						)}
					</div>

					<div className="profile__heading-name-section">
						<div className="profile__heading-name-section--left">
							<p className="profile__heading-name-section-title">
								{capitalizeName(teamData?.name)}
							</p>
							<p className="profile__heading-name-section-subtitle">
								{`${t(
									"sportData.sportsLabels." + teamData?.sport,
								)}${` • ${teamData?.league.name} (${teamData?.league.category})`}`}
							</p>
						</div>
					</div>

					<div className="follow-and-chat">
						{actorId !== params.teamId && params.teamId && (
							<>
								<FollowButton
									follow_id={params.teamId}
									onFollow={onFollow}
									onUnfollow={onUnfollow}
									size="l"
								/>
								<MsgButton memberId={params.teamId} />
							</>
						)}
					</div>

					<div className="profile__heading-info-section">
						<div className="profile__heading-divider"></div>
						<div className="d-flex justify-space-between mt-2">
							<p className="profile__heading-info-section-title">
								{`${t("common.words.location")}: `}
								<span className="profile__heading-info-section-title--gray">
									{teamData?.city}, {teamData?.country}
								</span>
							</p>
							<p className="profile__heading-info-section-title">
								{t("profile.profilePage.followersLabel")}{" "}
								<span
									onClick={() => {
										setFollowersModalOpen(true);
									}}
									className="profile__heading-info-section-title--gray"
								>
									{followers}
								</span>
							</p>
							<AcedModal
								state={{
									isModalOpen: isFollowersModalOpen,
									setModalOpen: setFollowersModalOpen,
								}}
							>
								{params.teamId && <FollowersModal user_id={params.teamId} />}
							</AcedModal>
						</div>
					</div>
				</div>

				{/* Edit Basic Info Modal */}
				<AcedModal
					state={{
						isModalOpen: isBasicInfoModalOpen,
						setModalOpen: setBasicInfoModalOpen,
					}}
					title="Edit Profile"
				>
					<div className="edit-basic-info-modal">
						<form onSubmit={handleSubmit(onBasicInfoSubmit)}>
							<div className="edit-basic-info-modal__fields">
								<AcedInput
									label="City"
									placeholder="City"
									control={control}
									name="city"
								/>
								<AcedInput
									label="Country"
									placeholder="Country"
									control={control}
									name="country"
								/>
								<AcedSelect
									label="Sport"
									placeholder="Select Sport"
									control={control}
									name="sport"
									options={sportOptions}
								/>
								{categories.length > 0 && (
									<AcedSelect
										label="Category"
										placeholder="Select Category"
										control={control}
										name="category"
										options={categories}
									/>
								)}
								{watchCategory && leagues.length > 0 && (
									<AcedSelect
										label="League"
										placeholder="Select League"
										control={control}
										name="league"
										options={leagues}
									/>
								)}
							</div>
							<div className="edit-basic-info-modal__actions">
								<Button
									className="btn btn-primary"
									action={() => setBasicInfoModalOpen(false)}
								>
									{t("profile.profilePage.cancelBtnLabel", "Cancel")}
								</Button>
								<Button
									className="btn btn-primary"
									type="submit"
								>
									{t("common.buttons.edit", "Save")}
								</Button>
							</div>
						</form>
					</div>
				</AcedModal>

				{manageBioField()}

				<div className="profile__experience-section">
					<div className="profile__experience-section-title d-flex justify-space-between">
						<p>{t("common.words.seasons")}</p>
						<div className="profile__stats-section-edit">
							{actorId === params.teamId && !newSeason && (
								<ClickIcon
									icon="add"
									onClick={addSeason}
									tooltip="Add new season"
									size={26}
								/>
							)}
						</div>
					</div>
					<div className="experiences">
						{seasonsArray &&
							seasonsArray.map((season, index) => (
								<SeasonCard
									isDirty={isDirty}
									info={season}
									key={index}
									register={register}
									removeSeasonAction={(index: number, id: any) =>
										removeSeasonAction(index, id)
									}
									setNewSeason={(value: boolean) => setNewSeason(value)}
									index={index}
									errors={errors}
									mySeason={actorId === params.teamId}
									teamId={params.teamId}
								/>
							))}
					</div>
				</div>

				<div className="profile__stats-section">
					<div className="d-flex justify-space-between">
						<p className="profile__stats-section-title">
							{t("profile.sections.data")}
						</p>
						<div className="profile__stats-section-edit">
							{actorId === params.teamId && (
								<ClickIcon
									icon="edit"
									onClick={() => setEditStats(true)}
									size={26}
								/>
							)}
						</div>
					</div>
					<div className="profile__stats-grid">
						{/* Team Information Section */}
						<div className="profile__stats-category">
							<div className="profile__stats-field">
								{editStats ? (
									<AcedInput
										placeholder="Website"
										control={control}
										name="website"
										className="profile__stats-input"
									/>
								) : (
									<div className="profile__stats-field-content">
										<span className="profile__stats-label">
											{t("createTeam.form.website.label")}
										</span>
										<span className="profile__stats-value">
											{teamData?.website ? (
												<a
													href={teamData.website}
													target="_blank"
													rel="noopener noreferrer"
												>
													{teamData.website}
												</a>
											) : (
												t("common.messages.notSpecified")
											)}
										</span>
									</div>
								)}
							</div>

							<div className="profile__stats-field">
								{editStats ? (
									<AcedInput
										placeholder="Slogan"
										control={control}
										name="slogan"
										className="profile__stats-input"
									/>
								) : (
									<div className="profile__stats-field-content">
										<span className="profile__stats-label">
											{t("createTeam.form.slogan.label")}
										</span>
										<span className="profile__stats-value">
											{teamData?.slogan || t("common.messages.notSpecified")}
										</span>
									</div>
								)}
							</div>

							<div className="profile__stats-field">
								{editStats && (
									<AcedSelect
										label={t("createTeam.fields.category")}
										control={control}
										name="category"
										options={categories}
										className="profile__stats-input"
									/>
								)}
							</div>

							<div className="profile__stats-field">
								{editStats ? (
									<AcedSelect
										label={t("common.words.league")}
										control={control}
										name="league"
										options={leagues}
										isDisabled={!watchCategory}
										className="profile__stats-input"
									/>
								) : (
									<div className="profile__stats-field-content">
										<span className="profile__stats-label">
											{t("common.words.league")}
										</span>
										<span className="profile__stats-value">
											{teamData?.league.name
												? `${teamData?.league.name} (${teamData?.league.category})`
												: t("common.messages.notSpecified")}
										</span>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="d-flex center profile__experience-section-edit-btn">
						{editStats && (
							<>
								<Button
									className="btn btn-primary"
									type="submit"
									action={() => setEditStats(false)}
								>
									{t("profile.profilePage.cancelBtnLabel")}
								</Button>

								<Button
									className="btn btn-primary"
									type="submit"
									action={handleSubmit(onStatsSubmit)}
								>
									{t("common.buttons.edit")}
								</Button>
							</>
						)}
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default TeamPage;
